import type { ContractedBook } from '@/data/types';
import { anmeldelseSnittFor, beregnBrageScore, type BrageSalgsBoost } from '@/systems/brage';

export type PrisId = 'bokhandlerprisen' | 'ungdommens' | 'p2-lytternes';

export type PrisSalgsBoost = BrageSalgsBoost;

export type PrisKategori = {
  id: string;
  navn: string;
  sjangre: ReadonlyArray<string>;
};

export type PrisVinnerEffekt = {
  omdomme: number;
  lojalitetBoost?: number;
  salgsBoostMult: number;
  salgsBoostDager: number;
  attentionBoost?: number;
};

export type PrisDefinition = {
  id: PrisId;
  navn: string;
  beskrivelse: string;
  topBorderHex: number;
  triggerDagIAaret: number;
  vinnerFaseOffsetDager: number;
  kategorier: ReadonlyArray<PrisKategori>;
  vinnerEffekt: PrisVinnerEffekt;
  kvalifiserteSjangre?: ReadonlyArray<string>;
  sjangerVekt?: Record<string, number>;
};

export type PrisNominasjon = {
  bokIsbn: string;
  tittel: string;
  forfatterNavn: string;
  forlag: 'spiller' | string;
  score: number;
  kategoriId: string;
};

export type PrisVinner = {
  prisId: PrisId;
  prisNavn: string;
  kategori: string;
  aar: number;
  dag: number;
  bokIsbn: string;
  bokTittel: string;
  forfatterNavn: string;
  forlag: 'spiller' | string;
};

export type PrisStatus = {
  prisId: PrisId;
  aar: number;
  fase: 'nominasjon' | 'vinner_annonsert' | 'avsluttet';
  triggetDag: number;
  nominerte: Record<string, PrisNominasjon[]>;
  vinnere: Record<string, PrisVinner | null>;
};

const ROMAN_SJANGRE: ReadonlyArray<string> = [
  'LITERARY',
  'KRIM',
  'FEELGOOD',
  'ROMANCE',
  'FANTASY',
];

export const PRIS_KATALOG: ReadonlyArray<PrisDefinition> = [
  {
    id: 'bokhandlerprisen',
    navn: 'Bokhandlerprisen',
    beskrivelse:
      'Norske bokhandlere kårer årets beste bok. Én vinner, alle sjangre, og et solid løft i butikkhyllene.',
    topBorderHex: 0x8b5e3c,
    triggerDagIAaret: 110,
    vinnerFaseOffsetDager: 5,
    kategorier: [
      { id: 'aapen', navn: 'Årets bok', sjangre: [] },
    ],
    vinnerEffekt: {
      omdomme: 15,
      salgsBoostMult: 2.0,
      salgsBoostDager: 45,
    },
  },
  {
    id: 'ungdommens',
    navn: 'Ungdommens kritikerpris',
    beskrivelse:
      'Norges videregående-elever leser, krangler og kårer. Ung lojalitet er gull verdt.',
    topBorderHex: 0x2c3e6b,
    triggerDagIAaret: 15,
    vinnerFaseOffsetDager: 5,
    kategorier: [
      {
        id: 'skjonn',
        navn: 'Skjønnlitteratur',
        sjangre: ['LITERARY', 'POETRY', 'KRIM', 'FANTASY', 'ROMANCE', 'FEELGOOD', 'BARNEBOK'],
      },
      {
        id: 'sakprosa',
        navn: 'Sakprosa',
        sjangre: ['NONFICTION'],
      },
    ],
    vinnerEffekt: {
      omdomme: 10,
      lojalitetBoost: 20,
      salgsBoostMult: 2.5,
      salgsBoostDager: 30,
    },
  },
  {
    id: 'p2-lytternes',
    navn: 'P2-lytternes romanpris',
    beskrivelse:
      'NRK P2s lytterskare stemmer fram årets roman. Folkelig, varmt, og en attention-bonanza.',
    topBorderHex: 0x3d5a4d,
    triggerDagIAaret: 40,
    vinnerFaseOffsetDager: 5,
    kategorier: [
      { id: 'roman', navn: 'Årets roman', sjangre: ROMAN_SJANGRE.slice() },
    ],
    vinnerEffekt: {
      omdomme: 15,
      salgsBoostMult: 2.0,
      salgsBoostDager: 30,
      attentionBoost: 5000,
    },
    kvalifiserteSjangre: ROMAN_SJANGRE,
  },
];

export function finnPris(id: PrisId): PrisDefinition | undefined {
  return PRIS_KATALOG.find((p) => p.id === id);
}

export function erKvalifisertForPris(
  sjanger: string,
  pris: PrisDefinition,
): boolean {
  if (!pris.kvalifiserteSjangre || pris.kvalifiserteSjangre.length === 0) {
    return true;
  }
  return pris.kvalifiserteSjangre.includes(sjanger.toUpperCase());
}

export function kategoriForBok(
  sjanger: string,
  pris: PrisDefinition,
): PrisKategori | null {
  const norm = sjanger.toUpperCase();
  for (const kat of pris.kategorier) {
    if (kat.sjangre.length === 0) return kat;
    if (kat.sjangre.includes(norm)) return kat;
  }
  return null;
}

export type PrisSpillerBokKandidat = {
  bok: ContractedBook;
  forfatterNavn: string;
};

export type PrisKonkurrentBokKandidat = {
  isbn: string;
  tittel: string;
  forfatterNavn: string;
  forlag: string;
  sjanger: string;
  anmeldelsesSnitt: number;
  salg: number;
};

const NOMINASJONER_PER_KATEGORI = 5;

export function velgPrisNominasjoner(
  pris: PrisDefinition,
  kategori: PrisKategori,
  spillerKandidater: ReadonlyArray<PrisSpillerBokKandidat>,
  konkurrentKandidater: ReadonlyArray<PrisKonkurrentBokKandidat>,
  spillerOmdomme: number,
): PrisNominasjon[] {
  const alle: Array<PrisNominasjon & { erSpiller: boolean }> = [];

  for (const kand of spillerKandidater) {
    if (!erKvalifisertForPris(kand.bok.genre, pris)) continue;
    const bokKat = kategoriForBok(kand.bok.genre, pris);
    if (!bokKat || bokKat.id !== kategori.id) continue;
    const snitt = anmeldelseSnittFor(kand.bok);
    let score = beregnBrageScore(snitt, kand.bok.salg, spillerOmdomme);
    if (pris.sjangerVekt) {
      const v = pris.sjangerVekt[kand.bok.genre.toUpperCase()] ?? 1;
      score *= v;
    }
    alle.push({
      bokIsbn: kand.bok.isbn,
      tittel: kand.bok.title,
      forfatterNavn: kand.forfatterNavn,
      forlag: 'spiller',
      score,
      kategoriId: kategori.id,
      erSpiller: true,
    });
  }

  for (const kand of konkurrentKandidater) {
    if (!erKvalifisertForPris(kand.sjanger, pris)) continue;
    const bokKat = kategoriForBok(kand.sjanger, pris);
    if (!bokKat || bokKat.id !== kategori.id) continue;
    let score = beregnBrageScore(kand.anmeldelsesSnitt, kand.salg, 50);
    if (pris.sjangerVekt) {
      const v = pris.sjangerVekt[kand.sjanger.toUpperCase()] ?? 1;
      score *= v;
    }
    alle.push({
      bokIsbn: kand.isbn,
      tittel: kand.tittel,
      forfatterNavn: kand.forfatterNavn,
      forlag: kand.forlag,
      score,
      kategoriId: kategori.id,
      erSpiller: false,
    });
  }

  alle.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.erSpiller && !b.erSpiller) return -1;
    if (!a.erSpiller && b.erSpiller) return 1;
    return 0;
  });

  return alle.slice(0, NOMINASJONER_PER_KATEGORI).map((n) => ({
    bokIsbn: n.bokIsbn,
    tittel: n.tittel,
    forfatterNavn: n.forfatterNavn,
    forlag: n.forlag,
    score: n.score,
    kategoriId: n.kategoriId,
  }));
}

export function velgPrisVinner(
  nominasjoner: ReadonlyArray<PrisNominasjon>,
  rng: () => number,
): PrisNominasjon | null {
  if (nominasjoner.length === 0) return null;
  const sortert = [...nominasjoner].sort((a, b) => b.score - a.score);
  if (rng() < 0.7) {
    return { ...sortert[0] };
  }
  const top3 = sortert.slice(0, Math.min(3, sortert.length));
  const idx = Math.floor(rng() * top3.length);
  return { ...top3[Math.min(top3.length - 1, idx)] };
}

export function tomNominertKart(
  pris: PrisDefinition,
): Record<string, PrisNominasjon[]> {
  const ut: Record<string, PrisNominasjon[]> = {};
  for (const k of pris.kategorier) ut[k.id] = [];
  return ut;
}

export function tomVinnerKartPris(
  pris: PrisDefinition,
): Record<string, PrisVinner | null> {
  const ut: Record<string, PrisVinner | null> = {};
  for (const k of pris.kategorier) ut[k.id] = null;
  return ut;
}

export function flaggPrisVinner(
  prisId: PrisId,
  aar: number,
  kategoriId: string,
): string {
  return `pris_vinner_${prisId}_${aar}_${kategoriId}`;
}

export function flaggPrisNominert(
  prisId: PrisId,
  aar: number,
  isbn: string,
): string {
  return `pris_nominert_${prisId}_${aar}_${isbn}`;
}

export function clonePrisStatus(s: PrisStatus): PrisStatus {
  const nom: Record<string, PrisNominasjon[]> = {};
  for (const [k, v] of Object.entries(s.nominerte)) {
    nom[k] = v.map((n) => ({ ...n }));
  }
  const vinn: Record<string, PrisVinner | null> = {};
  for (const [k, v] of Object.entries(s.vinnere)) {
    vinn[k] = v ? { ...v } : null;
  }
  return {
    prisId: s.prisId,
    aar: s.aar,
    fase: s.fase,
    triggetDag: s.triggetDag,
    nominerte: nom,
    vinnere: vinn,
  };
}

export function clonePrisNominertKart(
  k: Record<string, PrisNominasjon[]>,
): Record<string, PrisNominasjon[]> {
  const ut: Record<string, PrisNominasjon[]> = {};
  for (const [id, liste] of Object.entries(k)) {
    ut[id] = liste.map((n) => ({ ...n }));
  }
  return ut;
}

export function clonePrisVinnerKart(
  k: Record<string, PrisVinner | null>,
): Record<string, PrisVinner | null> {
  const ut: Record<string, PrisVinner | null> = {};
  for (const [id, v] of Object.entries(k)) {
    ut[id] = v ? { ...v } : null;
  }
  return ut;
}
